import React, { useState, useEffect } from "react";
import ReactCountryFlag from "react-country-flag";
import { usePageSettings } from "../../../../contexts/PageSettingsContext";
import DeleteServiceButton from "../forms/DeleteServiceButton";
import { useCheckout } from "../../../../contexts/CheckoutPageContext";

export const PlanDetails = ({ selectedPlan, selectedLocation, planPrice, totalPrice }) => {
	const { translations } = usePageSettings();

	// Access the context value
	const { orderCount, incrementOrderCount, decrementOrderCount } = useCheckout();

	const serviceLabel = translations[`serviceLabel.${selectedPlan.service}`] || "No plan Selected";
	const displayWebHostingPlane = translations[`webHostingPlan.${selectedPlan.name}`] || "N/A";

	const renderSharedUi = () => (
		<>
			<h3 className="font-bold text-xs text-neutral-50 mb-4">{translations["PlanDetailsTitle"]}</h3>
			{selectedPlan.service === "webhosting" ? (
				<div className="relative flex justify-between w-full items-center rounded-lg mb-3 text-ipxon-light-medium-gray text-sm">
					<div className="flex items-center gap-4">
						<span className="text-ipxon-magenta text-xs font-bold px-1.5 py-1 rounded outline outline-1 outline-offset-[-1px] outline-ipxon-magenta flex justify-center items-center gap-1.5">
							{translations[selectedPlan.service] || "No service Selected"}
						</span>
						<span className="text-neutral-50 text-xs font-bold px-1.5 py-1 rounded outline outline-1 outline-offset-[-1px] outline-neutral-50 h-fit capitalize">
							{displayWebHostingPlane || "Custom"}
						</span>
					</div>
					<DeleteServiceButton translations={translations} />
				</div>
			) : (
				<>
					<div className="flex items-center gap-4 mb-3">
						<span className="text-ipxon-magenta text-xs font-bold capitalize px-1.5 py-1 rounded outline outline-1 outline-offset-[-1px] outline-ipxon-magenta flex justify-center items-center gap-1.5">
							{/* {serviceLabelMap[selectedPlan.service] || "No plan Selected"} */}
							{serviceLabel}
						</span>
					</div>

					<div className="relative flex justify-between w-full pb-2 pl-1 items-center rounded-lg mb-3 text-ipxon-light-medium-gray text-sm">
						<div className="flex gap-2">
							<ReactCountryFlag
								style={{
									fontSize: "1.5em",
									lineHeight: "1.5em",
									borderRadius: "80%",
									objectFit: "cover",
								}}
								countryCode={selectedLocation.iso_code}
								svg
							/>
							{selectedLocation.name}
						</div>
						<DeleteServiceButton translations={translations} />
					</div>
				</>
			)}
		</>
	);

	// 2) Define service-specific UI in a switch
	const renderServiceDetails = () => {
		switch (selectedPlan.service) {
			case "vps":
			case "baremetal":
				// Show CPU, RAM, Storage, etc.
				return (
					<div className="flex gap-4 pb-7">
						<ul className="flex flex-col gap-2 text-zinc-400 text-sm font-normal capitalize w-fit">
							<li>{translations["PlanDetailsCPU"]}</li>
							<li>{translations["PlanDetailsRAM"]}</li>
							<li>{translations["PlanDetailsDisk"]}</li>
							<li>{translations["PlanDetailsSpeed"]}</li>
							<li>{translations["PlanDetailsBandwidth"]}</li>
						</ul>
						<ul className="flex flex-col gap-2 text-sm text-zinc-200">
							<li>{selectedPlan.cpu?.value}</li>
							<li>{selectedPlan.ram?.value}</li>
							<li>{selectedPlan.storage?.value}</li>
							<li>{selectedPlan.bandwidth?.value}</li>
							<li>{selectedPlan.transfer?.value}</li>
						</ul>
					</div>
				);

			case "bgp":
				return (
					// <div className="grid grid-cols-2 pb-7">
					// 	<ul className="flex flex-col gap-2 text-zinc-400 text-sm font-normal capitalize">
					// 		<li>BGP:</li>
					// 	</ul>
					// 	<ul className="flex flex-col gap-2 text-sm text-zinc-200"></ul>
					// </div>
					null
				);
			case "iptransit":
				return (
					<div className="grid grid-cols-2 pb-7">
						<ul className="flex flex-col gap-2 text-zinc-400 text-sm font-normal capitalize">
							<li>{translations["PlanDetailsBandwidth"]}</li>
						</ul>
						<ul className="flex flex-col gap-2 text-sm text-zinc-200">
							<li>{selectedPlan.bandwidth?.value}</li>
						</ul>
					</div>
				);
			case "storage":
				return (
					<div className="grid grid-cols-2 pb-7">
						<ul className="flex flex-col gap-2 text-zinc-400 text-sm font-normal capitalize">
							<li>NAS:</li>
						</ul>
						<ul className="flex flex-col gap-2 text-sm text-zinc-200">
							<li>{selectedPlan.nas?.value}</li>
						</ul>
					</div>
				);
			case "colocation":
				return (
					<div className="grid grid-cols-2 pb-7">
						<ul className="flex flex-col gap-2 text-zinc-400 text-sm font-normal capitalize">
							<li>{translations["PlanDetailsRack"]}</li>
							<li>{translations["PlanDetailsEnergy"]}</li>
							<li>{translations["PlanDetailsSpeed"]}</li>
							<li>{translations["PlanDetailsPrefix"]}</li>
						</ul>
						<ul className="flex flex-col gap-2 text-sm text-zinc-200">
							<li>{selectedPlan.rack?.value}</li>
							<li>{selectedPlan.energy?.value}</li>
							<li>{selectedPlan.bandwidth?.value}</li>
							<li>{selectedPlan.prefix?.value}</li>
						</ul>
					</div>
				);
			case "webhosting":
				return (
					<div className="grid grid-cols-1 gap-2 pb-7">
						{/* <p className="flex flex-col gap-2 text-zinc-400 text-sm font-normal capitalize">
							{selectedPlan.name}
						</p> */}
						{selectedPlan.webFeatures && (
							<ul className="flex flex-col gap-2 text-sm text-zinc-200">
								{selectedPlan.webFeatures.map((feature, id) => (
									<li key={id}>{translations[feature]}</li>
								))}
							</ul>
						)}
					</div>
				);

			default:
				// Fallback if service is unknown
				return null;
		}
	};

	// 3) Combine everything in your return
	return (
		<div className="flex flex-col">
			{/* Common part */}
			{renderSharedUi()}

			{/* Service-specific details */}
			{renderServiceDetails()}

			<div className="w-full flex justify-between items-center pb-7 border-b border-ipxon-gradient-black-lighter">
				<span className="flex w-full gap-4 items-center">
					<button
						type="button"
						className="size-[30px] rounded text-center text-zinc-500 border border-zinc-500 text-sm font-normal leading-[18px] items-center flex justify-center"
						onClick={() => decrementOrderCount()}
					>
						-
					</button>
					{orderCount}
					<button
						type="button"
						className="size-[30px] rounded text-center text-zinc-500 border border-zinc-500 text-sm font-normal leading-[18px] items-center flex justify-center"
						onClick={() => incrementOrderCount()}
					>
						+
					</button>
				</span>
				<span className="uppercase text-sm text-zinc-200 w-full text-end">
					{["storage", "ultraproxy", "iptransit", "bgp"].includes(selectedPlan?.service?.toLowerCase()) &&
						(!selectedPlan?.id || selectedPlan.id === "")
						? totalPrice
						: planPrice}
				</span>
			</div>
		</div>
	);
};
